import { useMobile } from "@/hooks";
import { getLocalTimeFormatDate } from "@/utils";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import {
  Button,
  Toast,
  Toggle,
  WarningCircular,
} from "@monsterindia/global-ui-components";
import { Whatsapp } from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";

import {
  editWhatsappPreferences,
  getNotificationUpdate,
  notificationUpdate,
} from "@/components/settings/api/networkUtils";
import { getNotificationsData } from "@/components/settings/contents/EmailNotifications";

import { OptionsProps } from "../../types/setting";
import {
  fetchUnSubscribeOutReachDataAPI,
  unSubscribeOutReachAPI,
} from "../../unsubscribe/query";
import { UnsubscribeFormData } from "../../unsubscribe/types/unsubscribe";
import { OutreachSubscriptionState } from "../../unsubscribe/types/unsubscribe";
import ResubscribeCompaniesModal from "./ResubscribeCompaniesModal";
import OutreachSubscriptionModal from "./outreachSubscriptionModal";

const EmailNotifications = () => {
  const isMobile = useMobile();
  const [toast, setToast] = useState({ text: "", type: "", show: false });
  const [notificationChange, setNotificationChange] = useState({
    status: false,
    currentOption: "",
    value: false,
  });
  const [options, setOptions] = useState<OptionsProps>({
    recommendedJobs: false,
    applicationStatus: false,
    outreachSubscription: false,
    profileViews: false,
    improveProfileStrength: false,
    whatsAppNotifications: false,
    careerAdviceAndTips: false,
    promotionsAndSpecialOffers: false,
  });
  const [outreachSubscriptionData, setOutreachSubscriptionData] =
    useState<OutreachSubscriptionState>({
      data: {
        action: "",
        scope: "",
        duration_months: -1,
        companies: [],
      },
      currentUnsubscribeData: {
        success: false,
        data: {
          company_consents: [],
          global_consent: {
            subscribed: false,
            expiry_at: "",
          },
        },
      },
      unsubscribeModalOpen: false,
      resubscribeModalOpen: false,
      unsubscribeSuccess: false,
      unsubscribeSuccessMessage: "",
      resubscribeSuccess: false,
      resubscribeSuccessMessage: "",
    });
  const emailNotificationsData = getNotificationsData(options);
  const handleToast = (message: string, type: string) => {
    setToast({ text: message, type, show: true });
  };

  useEffect(() => {
    if (notificationChange?.status) {
      notificationAPICalls(
        notificationChange?.currentOption,
        notificationChange?.value,
      );
    }
  }, [notificationChange]);

  useEffect(() => {
    const fetchNotificationData = async () => {
      const response = await Promise.allSettled([
        getNotificationUpdate(),
        fetchUnSubscribeOutReachDataAPI(),
      ]);
      let newOptions: OptionsProps = options;

      if (response[0].status === "fulfilled") {
        newOptions = {
          recommendedJobs: response[0]?.value?.jobNotification?.recommendedJobs,
          applicationStatus:
            response[0]?.value?.jobNotification?.applicationStatus,
          outreachSubscription: false,
          profileViews:
            response[0]?.value?.profileRelatedNotification?.profileViews,
          improveProfileStrength:
            response[0]?.value?.profileRelatedNotification
              ?.improveProfileStrength,
          whatsAppNotifications: response[0]?.value?.whatsappStatus,
          careerAdviceAndTips: response[0]?.value?.careerAdviceAndTips,
          promotionsAndSpecialOffers:
            response[0]?.value?.promotionsAndSpecialOffers,
        };
      }
      if (response[1].status === "fulfilled") {
        newOptions = {
          ...newOptions,
          outreachSubscription:
            response[1]?.value?.data?.global_consent?.subscribed ?? false,
        };
        let currentUnsubscribeDataResp =
          (response[1].status === "fulfilled" && response[1]?.value) ||
          undefined;
        let currentUnsubscribeTemp =
          currentUnsubscribeDataResp?.data?.company_consents;
        let filteredCompanyConsents =
          currentUnsubscribeTemp && currentUnsubscribeTemp.length > 0
            ? currentUnsubscribeTemp.filter((company) => !company.subscribed)
            : [];
        if (currentUnsubscribeDataResp) {
          setOutreachSubscriptionData(
            (prevData: OutreachSubscriptionState) => ({
              ...prevData,
              currentUnsubscribeData: {
                success: currentUnsubscribeDataResp.success ?? false,
                data: {
                  company_consents: filteredCompanyConsents,
                  global_consent: {
                    subscribed:
                      currentUnsubscribeDataResp.data?.global_consent
                        ?.subscribed ?? false,
                    expiry_at:
                      currentUnsubscribeDataResp.data?.global_consent
                        ?.expiry_at ?? "",
                  },
                },
              },
            }),
          );
        }
      }
      setOptions(newOptions);
    };
    fetchNotificationData();
  }, []);

  useEffect(() => {
    if (outreachSubscriptionData.unsubscribeSuccess) {
      setOptions((preState: OptionsProps) => ({
        ...preState,
        outreachSubscription: false,
      }));
      setNotificationChange({
        status: true,
        currentOption: "outreachSubscription",
        value: false,
      });
      setOutreachSubscriptionData({
        ...outreachSubscriptionData,
        unsubscribeSuccess: false,
        unsubscribeModalOpen: false,
      });
    }
    if (outreachSubscriptionData.resubscribeSuccess) {
      setOutreachSubscriptionData({
        ...outreachSubscriptionData,
        resubscribeSuccess: false,
        resubscribeModalOpen: false,
      });
      handleToast(
        outreachSubscriptionData?.resubscribeSuccessMessage,
        "success",
      );
    }
  }, [
    outreachSubscriptionData.resubscribeSuccess,
    outreachSubscriptionData.unsubscribeSuccess,
  ]);

  const onChangeOption = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === "whatsAppNotifications") {
      updateWhatsappPreferences(e.target.name, e.target.checked);
      return;
    }

    if (e.target.name === "outreachSubscription" && !e.target.checked) {
      setOutreachSubscriptionData({
        ...outreachSubscriptionData,
        unsubscribeModalOpen: true,
      });
      return;
    }

    setOptions((preState: OptionsProps) => ({
      ...preState,
      [e.target.name]: e.target.checked,
    }));
    setNotificationChange({
      status: true,
      currentOption: e.target.name,
      value: e.target.checked,
    });
  };

  const onResubscribeClick = async () => {
    setOutreachSubscriptionData({
      ...outreachSubscriptionData,
      resubscribeModalOpen: true,
    });
  };

  const updateWhatsappPreferences = async (
    currentOption: string,
    value: boolean,
  ) => {
    try {
      const data = await editWhatsappPreferences(value);
      trackClevertapEvents.eventPush("Settings Updated", {
        whatsAppNotification: value ? "Enabled" : "Disabled",
      });

      if (data.status == 201) {
        setOptions((preState) => ({
          ...preState,
          [currentOption]: value,
        }));
        handleToast("Email Preferences saved Successfully", "success");
      }
    } catch (err) {
      handleToast("Something went wrong.", "error");
    }
  };

  const notificationAPICalls = async (
    currentOption: string,
    value: boolean,
  ) => {
    try {
      let data;
      if (currentOption !== "outreachSubscription") {
        data = await notificationUpdate(options);
      } else if (!value) {
        data = {
          successMessage: outreachSubscriptionData.unsubscribeSuccessMessage,
        };
      } else if (value) {
        const formData: UnsubscribeFormData = {
          action: "subscribe",
          scope: "global",
          duration_months: -1,
        };

        const response: any = await unSubscribeOutReachAPI(formData);
        data = {
          successMessage: response?.message || "",
        };
        trackClevertapEvents.eventPush("ResubscribeType", {
          UnsubscriptionType: "Recruiter3M",
        });
      }
      trackClevertapEvents.eventPush("Settings Updated", {
        [currentOption]: value ? "Enabled" : "Disabled",
      });
      if (data) {
        setOptions((preState: OptionsProps) => ({
          ...preState,
          [currentOption]: value,
        }));
        handleToast(data?.successMessage, "success");
      }
    } catch (err) {
      setOptions((preState: OptionsProps) => ({
        ...preState,
        [currentOption]: !value,
      }));
      handleToast("Something went wrong.", "error");
    }
  };

  return (
    <>
      {outreachSubscriptionData.unsubscribeModalOpen && (
        <OutreachSubscriptionModal
          outreachSubscriptionData={outreachSubscriptionData}
          setOutreachSubscriptionData={setOutreachSubscriptionData}
        />
      )}
      {outreachSubscriptionData.resubscribeModalOpen && (
        <ResubscribeCompaniesModal
          outreachSubscriptionData={outreachSubscriptionData}
          setOutreachSubscriptionData={setOutreachSubscriptionData}
        />
      )}
      <Toast
        timer={3000}
        showToast={toast.show}
        text={toast.text}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        width={isMobile ? "75" : "50"}
        position="top-center"
      />
      <div className="flex flex-col items-start justify-center gap-y-2.5">
        {emailNotificationsData?.map((notification) => {
          return (
            <div
              key={notification?.title || "Untitled Notification"}
              className="bg-surface-secondary flex flex-col items-start justify-center gap-6 self-stretch rounded-lg px-3 py-4"
            >
              <div className="flex flex-col justify-center gap-2 self-stretch">
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-start gap-1">
                    {notification?.icon && (
                      <Whatsapp
                        width={24}
                        height={24}
                        className="text-[#64B161]"
                      />
                    )}
                    <div className="text-base font-bold leading-normal">
                      {notification?.title || "Untitled Notification"}
                    </div>
                  </div>
                  {notification?.showToggle && (
                    <Toggle
                      size="small"
                      type="checkbox"
                      id={notification?.id}
                      name={notification?.name}
                      checked={notification?.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChangeOption(e)
                      }
                    />
                  )}
                </div>
                <div className="text-sm font-normal leading-tight text-gray-600">
                  {notification?.description || "No description available"}
                </div>
              </div>
              {notification?.options && (
                <div className="background-white flex w-full flex-col gap-2.5 rounded-lg bg-white p-3">
                  <div className="flex flex-col bg-white">
                    {notification?.options.map((option, index) => {
                      return (
                        <div className="flex flex-col">
                          <div
                            className={`flex w-full items-center justify-between gap-2 bg-white py-3 ${index !== notification?.options.length - 1 ? "border-b border-gray-300" : ""}`}
                            key={option?.title}
                          >
                            <div className="shrink grow basis-0 text-sm font-normal leading-tight">
                              {option?.title || "Untitled Option"}{" "}
                              {option?.id ===
                                "jobNotification.outreachSubscription" &&
                                !option.value &&
                                outreachSubscriptionData?.currentUnsubscribeData
                                  ?.data?.global_consent?.expiry_at && (
                                  <span className="text-sm font-bold italic leading-5 text-[#1D1934]">
                                    (Unsubscribed till{" "}
                                    {getLocalTimeFormatDate(
                                      outreachSubscriptionData
                                        .currentUnsubscribeData.data
                                        .global_consent.expiry_at,
                                    )}
                                    )
                                  </span>
                                )}
                            </div>
                            <div className="flex w-10 flex-col">
                              <Toggle
                                size="small"
                                type="checkbox"
                                id={option?.id}
                                name={option?.name}
                                checked={option?.value}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>,
                                ) => onChangeOption(e)}
                              />
                            </div>
                          </div>
                          {option.id ===
                            "jobNotification.outreachSubscription" &&
                          outreachSubscriptionData?.currentUnsubscribeData?.data
                            ?.company_consents?.length > 0 &&
                          option.value ? (
                            <div className="my-3 flex flex-col justify-center gap-[6px] rounded-md bg-[#FFF6E5] px-3 py-[6px] md:flex-row md:!items-center md:!justify-between">
                              <div className="flex items-center gap-[6px]">
                                <WarningCircular width={20} height={20} />
                                <div className="flex-1 text-xs font-medium leading-4 text-[#1D1934]">
                                  Re-subscribe to selected recruiters or all
                                  recruiters
                                </div>
                              </div>

                              <Button
                                type="submit"
                                modifier="primary"
                                label="Re-subscribe"
                                className="ml-[26px] h-[32px] w-[117px] px-3 py-2 text-center text-xs font-normal leading-4 text-[#FFF] md:ml-0"
                                onClick={onResubscribeClick}
                                size=""
                              />
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};
export default EmailNotifications;
